import type { DragonDossier, ValidationIssue } from "../types";

export function repairDossier(input: DragonDossier): DragonDossier {
  const dossier = structuredClone(input);
  const roomIds = new Set(dossier.lair.rooms.map((room) => room.id));
  dossier.lair.connections = dossier.lair.connections.filter(
    (connection) =>
      connection.from !== connection.to &&
      roomIds.has(connection.from) &&
      roomIds.has(connection.to)
  );

  const distribution = dossier.hoard.distribution;
  const sum = distribution.visible + distribution.hidden + distribution.claimed + distribution.cursed;
  if (sum !== 100) {
    distribution.hidden = Math.max(0, distribution.hidden + (100 - sum));
  }

  const factionIds = new Set(dossier.factions.map((faction) => faction.id));
  dossier.relationships = dossier.relationships.filter(
    (relationship) =>
      (relationship.source === "dragon" || factionIds.has(relationship.source)) &&
      (relationship.target === "dragon" || factionIds.has(relationship.target))
  );

  if (!dossier.lair.rooms.some((room) => room.role === "sanctum")) {
    dossier.lair.rooms.push({
      id: `room-repaired-sanctum`,
      name: "The Inner Sanctum",
      role: "sanctum",
      danger: "mythic",
      occupants: "The dragon",
      hazards: ["unstable terrain"],
      treasure: "The deepest reserve",
      tacticalNote: "The dragon controls every approach.",
      x: 76,
      y: 44,
      radius: 8
    });
  }

  return dossier;
}

export function validateDossier(dossier: DragonDossier): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const roomIds = new Set(dossier.lair.rooms.map((room) => room.id));
  const connected = new Set<string>();
  dossier.lair.connections.forEach((connection) => {
    connected.add(connection.from);
    connected.add(connection.to);
    if (!roomIds.has(connection.from) || !roomIds.has(connection.to)) {
      issues.push({
        id: `connection-${connection.id}`,
        severity: "error",
        facet: "lair",
        message: `Connection ${connection.id} refers to a missing room.`
      });
    }
  });

  for (const role of ["approach", "hoard", "sanctum"] as const) {
    const room = dossier.lair.rooms.find((candidate) => candidate.role === role);
    if (!room) {
      issues.push({
        id: `missing-${role}`,
        severity: "error",
        facet: "lair",
        message: `The lair needs a ${role} room.`
      });
    } else if (!connected.has(room.id) && dossier.lair.rooms.length > 1) {
      issues.push({
        id: `isolated-${role}`,
        severity: "warning",
        facet: "lair",
        message: `${room.name} is not connected to the room graph.`
      });
    }
  }

  const distribution = dossier.hoard.distribution;
  const total = distribution.visible + distribution.hidden + distribution.claimed + distribution.cursed;
  if (total !== 100) {
    issues.push({
      id: "hoard-total",
      severity: "error",
      facet: "hoard",
      message: `Hoard distribution totals ${total}% instead of 100%.`
    });
  }

  if (dossier.history.length < 5) {
    issues.push({
      id: "history-depth",
      severity: "warning",
      facet: "history",
      message: "A finished dossier should contain at least five history events."
    });
  }

  if (dossier.factions.length < 3) {
    issues.push({
      id: "faction-depth",
      severity: "warning",
      facet: "factions",
      message: "Add at least three factions for a useful political web."
    });
  }

  const actionNames = new Set(dossier.rules.actions.map((action) => action.name.toLowerCase()));
  for (const phase of dossier.encounterScript) {
    if (phase.kind === "combat" && actionNames.size === 0) {
      issues.push({
        id: `combat-actions-${phase.id}`,
        severity: "error",
        facet: "encounter",
        message: "Combat phases require at least one rule-profile action."
      });
    }
  }

  return issues;
}
